import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createVNode as _createVNode, withCtx as _withCtx, createSlots as _createSlots, openBlock as _openBlock, createBlock as _createBlock } from "vue"

import MeasurementCard, { Coloring } from './MeasurementCard.vue'
import { computed, toRefs } from 'vue'
import { FormatOptions } from '../formatter'
import { ColorScaleConfig } from '@/utils/colorScale'
import { EntityLevel } from '@/composables/useEntityLevel'
import ColorLegend from '@/design-system/atoms/ColorLegend.vue'
import TableDataSource, { Table } from './TableDataSource.vue'
import { useDataSource } from '@/composables/useDataSources'
import { LocaleString } from '@/api-client/models/utilization/Contracts'
import { TextType } from '@/design-system/atoms/TextBase.vue'
import { useComponentFilter } from '../measurements/useMeasurements'
import { Feature } from '@/pinia/feature'

export type ValueType = 'number' | 'gauge'

export type Value<Type extends ValueType = ValueType> = Type extends 'number'
  ? {
      type: 'number'
      dataSource: string
      textType?: TextType
      format?: FormatOptions
    }
  : {
      type: 'gauge'
      dataSource: string
      min?: number
      max?: number
      format?: FormatOptions
    }

export type ColoredEntities = {
  dataSource: string
  enabledEntityLevels?: EntityLevel[]
}

export type ColorLegend = {
  width?: number
  height?: number
  padding?: number
  hideMean?: boolean
  format?: FormatOptions
  labelStart?: LocaleString
  labelEnd?: LocaleString
}

export type TableConfig = {
  dataSource: string
  sort?: { col: number; direction: 'asc' | 'desc'; field: string }
  headers: LocaleString[]
}

type Props = {
  title: string
  info?: string
  value?: Value
  colorScale?: ColorScaleConfig
  color?: string
  coloredEntities?: ColoredEntities
  colorLegend?: ColorLegend
  initialShowOnMap?: boolean
  table?: TableConfig
  featureName: string
  children?: Feature[]
}


export default /*@__PURE__*/_defineComponent({
  __name: 'MainMeasurementCard',
  props: {
    title: {},
    info: { default: undefined },
    value: { default: undefined },
    colorScale: { default: undefined },
    color: { default: undefined },
    coloredEntities: { default: undefined },
    colorLegend: { default: () => ({
    height: 60,
    padding: 20,
    hideMean: false
  }) },
    initialShowOnMap: { type: Boolean, default: false },
    table: { default: undefined },
    featureName: {},
    children: { default: () => [] }
  },
  setup(__props: any) {

const props = __props

const { value: valueConf, coloredEntities, table: tableConfig } = toRefs(props)

useComponentFilter(props.featureName, {})

const valueDataSource = useDataSource<number>(valueConf.value?.dataSource)
const tableDS = useDataSource<Table['rows']>(tableConfig.value?.dataSource)
const coloringDataSource = useDataSource<Coloring[]>(coloredEntities.value?.dataSource)

const valueNumber = computed<number | undefined>(() => valueDataSource.value?.data)
const tableRows = computed<Table['rows'] | undefined>(() => tableDS.value?.data)
const colorings = computed<Coloring[] | undefined>(() => coloringDataSource.value?.data)

const table = computed<Table>(() => ({
  sort: {
    col: tableConfig.value?.sort?.col ?? 0,
    direction: tableConfig.value?.sort?.direction ?? 'asc',
    field: tableConfig.value?.sort?.field ?? 'text'
  },
  headers: tableConfig.value?.headers ?? [],
  rows: tableRows.value ?? []
}))

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createBlock(MeasurementCard, {
    title: _ctx.title,
    info: _ctx.info,
    value: 
      valueNumber.value !== undefined
        ? _ctx.value?.type === 'gauge'
          ? {
              type: 'gauge',
              number: valueNumber.value,
              min: _ctx.value?.min,
              max: _ctx.value?.max,
              format: _ctx.value?.format,
              isLoading: _unref(valueDataSource)?.isLoading ?? false
            }
          : {
              type: 'number',
              number: valueNumber.value,
              format: _ctx.value?.format,
              textType: _ctx.value?.textType,
              isLoading: _unref(valueDataSource)?.isLoading ?? false
            }
        : {
            type: 'string',
            string: '-',
            isLoading: _unref(valueDataSource)?.isLoading ?? false,
            textType: _ctx.value && 'textType' in _ctx.value ? _ctx.value.textType : undefined
          }
    ,
    color: _ctx.color,
    colorScale: _ctx.colorScale,
    colorLegend: _ctx.colorLegend,
    coloredEntities: {
      ..._unref(coloredEntities),
      colorings: colorings.value
    },
    initialShowOnMap: _ctx.initialShowOnMap
  }, _createSlots({ _: 2 }, [
    (table.value && table.value.headers.length && table.value.rows.length > 0)
      ? {
          name: "details",
          fn: _withCtx(() => [
            _createVNode(TableDataSource, { table: table.value }, null, 8, ["table"])
          ]),
          key: "0"
        }
      : undefined
  ]), 1032, ["title", "info", "value", "color", "colorScale", "colorLegend", "coloredEntities", "initialShowOnMap"]))
}
}

})